<template>
  <div>
    <b-overlay
      :show="vpnServersResponses('vpnServersList') && vpnServersEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            VPN Servers
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>VPN Servers</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="vpnServersResponses('vpnServer') || vpnServersResponses('manageVpnServer')"
            @click="unselectVpnServer(), showModal('manageModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New VPN Server</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="vpnServersList"
            :fields="fields"
            :class="{ 'table-hover' : !vpnServersEmpty }"
            stacked="md"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(active)="data">
              <div class="d-flex justify-center-start justify-content-md-center">
                <!-- Wrapper for fake element + checkbox -->
                <div
                  class="position-relative"
                  style="width: fit-content;">
                  <!-- Fake element above checkbox that TRIGGERS event -->
                  <div
                    class="w-100 h-100 position-absolute"
                    style="z-index: 20;"
                    role="button"
                    @click="handleStatusChange(data.item)" />
                  <!-- Checkbox that REPRESENTS current status, not changes it -->
                  <b-form-checkbox
                    :checked="data.item.active"
                    size="lg"
                    switch
                    name="switch-vpn-server"
                    class="switch-success ml-0 ml-md-3 mb-1 mb-md-2" />
                </div>
              </div>
            </template>
            <template #cell(edit)="data">
              <b-button
                size="sm"
                v-wave
                variant="outline-primary"
                class="mr-4 align-middle"
                :disabled="vpnServersResponses('vpnServer') || vpnServersResponses('manageVpnServer')"
                @click="openVpnServerParams(data.item._id)">
                <i class="fa fa-pen fa-sm"></i>
                <span>Edit</span>
              </b-button>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="vpnServersResponses('deleteVpnServer')"
                @click="selectServer(data.item), showDeletePopover(data.item._id)">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="vpnServer in vpnServersList"
      :key="`popover-${vpnServer._id}`"
      :target="`deleteButton-${vpnServer._id}`"
      :ref="`popover-${vpnServer._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ vpnServer.name }}</span>» VPN Server?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(vpnServer._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(vpnServer._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <b-modal
      ref="manageModal"
      centered
      size="lg"
      :title="vpnServersModalTitle"
      hide-header-close
      footer-bg-variant=""
      scrollable
      no-close-on-esc
      @hide="onHideAttempt"
      @hidden="unselectVpnServer(), clearErrors()">
      <b-form @submit.stop.prevent>
        <b-form-group
          id="name-vpnServer"
          label="Name"
          label-for="name-vpnServer">
          <b-form-input
            id="name-vpnServer"
            name="name-vpnServer"
            :disabled="selectedVpnServerExist"
            :class="{ 'cursor-not-allowed' : selectedVpnServerExist }"
            v-model="selectedVpnServer.name"
            :state="getValidationState('name')"
            aria-describedby="name-feedback"
            placeholder="Open VPN" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageVpnServerError('name')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="code-vpnServer"
          label="Code"
          label-for="code-vpnServer">
          <b-form-input
            id="code-vpnServer"
            name="code-vpnServer"
            :disabled="selectedVpnServerExist"
            :class="{ 'cursor-not-allowed' : selectedVpnServerExist }"
            v-model="selectedVpnServer.code"
            :state="getValidationState('code')"
            aria-describedby="code-feedback"
            placeholder="openvpn" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageVpnServerError('code')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group v-if="!selectedVpnServerExist">
          <b-form-checkbox
            id="active-checkbox"
            v-model="selectedVpnServer.active"
            name="active-checkbox">
            Activate VPN server after adding it
          </b-form-checkbox>
        </b-form-group>

        <div class="font-weight-bold mb-2">Parameters (optional)</div>

        <b-alert
          :show="selectedVpnServer.parameters.length > 0"
          variant="light-warning"
          class="mb-8">
          You enter all parameters at your own risk. They can affect the performance of the entire system. Be very careful when adding or changing these parameters.
        </b-alert>

        <b-form-group
          v-for="(parameter, index) in selectedVpnServer.parameters"
          :key="`dataset-${index}`"
          :class="{ 'mb-3' : index === selectedVpnServer.parameters.length - 1 }">
          <b-row>
            <b-col
              col
              lg="4"
              md="4"
              sm="3"
              cols="3"
              class="pr-2 pr-md-4">
              <b-form-input
                v-model="parameter.name"
                placeholder="Key"
                class="mb-2" />
              <b-form-checkbox v-model="parameter.isPublic">
                Send to agent
              </b-form-checkbox>
            </b-col>
            <b-col
              col
              lg="8"
              md="8"
              sm="9"
              cols="9"
              class="d-flex pl-2 pl-md-4">
              <b-form-textarea
                v-model="parameter.code"
                rows="4"
                no-resize
                placeholder="Value" />
              <b-button
                variant="outline-primary"
                class="btn-icon ml-6"
                size="sm"
                style="min-width: calc(1.35em + 1.1rem + 2px);"
                @click="deleteParameter(index)">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </b-col>
          </b-row>
        </b-form-group>

        <div class="d-flex">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="addNewParameter(selectedVpnServer.parameters.length)">
            <i class="fa fa-plus fa-sm"></i>
            Add New
          </b-button>
        </div>
      </b-form>

      <template #modal-footer>
        <div class="d-flex w-100">
          <b-button
            v-b-tooltip.hover.right
            ref="cancelManageModal"
            title="Are you sure? Unsaved data will be lost."
            variant="outline-secondary"
            size="md"
            @click="hideModal('manageModal')">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            v-wave
            size="md"
            class="ml-auto"
            @click="submitVpnServer()">
            Submit
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      ref="confirmModal"
      centered
      size="md"
      title="Wait! Are you sure?"
      header-text-variant="danger"
      hide-header
      scrollable
      @hidden="unmarkForSwitch()">

      <b-alert
        variant="light-danger"
        show
        class="mb-8">
        <p class="font-size-lg mb-0">
          Shutting down VPN Server «<span class="font-weight-bold">{{ markedForSwitchVpnServer.name }}</span>» can lead to unexpected outcomes and cause problems for users. Think twice before doing this!
        </p>
      </b-alert>

      <b-list-group class="mb-0">
        <b-list-group-item class="d-flex align-items-center">
          Name
          <span class="ml-auto">{{ markedForSwitchVpnServer.name }}</span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Code
          <span class="ml-auto">{{ markedForSwitchVpnServer.code }}</span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Total Tariffs
          <b-badge
            variant="light"
            class="ml-auto"
            pill>{{ markedForSwitchVpnServer.total_tariffs }}</b-badge>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Total Servers
          <b-badge
            variant="light"
            class="ml-auto"
            pill>{{ markedForSwitchVpnServer.total_servers }}</b-badge>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Total Users
          <b-badge
            variant="light"
            class="ml-auto"
            pill>{{ markedForSwitchVpnServer.total_users }}</b-badge>
        </b-list-group-item>
      </b-list-group>

      <template #modal-footer>
        <div class="d-flex w-100">
          <b-button
            variant="outline-secondary"
            size="md"
            @click="hideModal('confirmModal')">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            size="md"
            class="ml-auto"
            @click="switchVpnServer(markedForSwitchVpnServer._id), hideModal('confirmModal')">
            Yes, turn it off
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  // actions
  GET_VPN_SERVERS,
  GET_VPN_SERVER,
  SWITCH_VPN_SERVER,
  MANAGE_VPN_SERVER,
  DELETE_VPN_SERVER,

  // mutations
  SELECT_VPN_SERVER,
  UNSELECT_VPN_SERVER,
  MARK_FOR_SWITCH_VPN_SERVER,
  UNMARK_FOR_SWITCH_VPN_SERVER,
  ADD_NEW_VPN_SERVER_PARAM,
  DELETE_VPN_SERVER_PARAM,
  CLEAR_VPN_SERVERS,
  CLEAR_MANAGE_VPN_SERVER_ERRORS,
} from '@/core/services/store/vpn-servers.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { toastOptions } from '@/main'

export default {
  name: 'SettingsVpnServers',
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold', 'text-nowrap', 'pl-5'],
        },
        {
          label: 'Code',
          key: 'code',
          class: ['align-middle', 'text-nowrap'],
        },
        {
          label: 'Status',
          key: 'active',
          class: ['align-middle', 'text-nowrap', 'text-center'],
          thStyle: 'width: 200px;',
        },
        {
          label: 'Tariffs',
          key: 'total_tariffs',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          thStyle: 'width: 130px;',
        },
        {
          label: 'Servers',
          key: 'total_servers',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          thStyle: 'width: 130px;',
        },
        {
          label: 'Users',
          key: 'total_users',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          thStyle: 'width: 130px;',
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
        }
      ],
    }
  },
  computed: {
    ...mapGetters([
      'vpnServersList',
      'vpnServersEmpty',
      'totalVpnServers',
      'selectedVpnServer',
      'selectedVpnServerExist',
      'markedForSwitchVpnServer',
      'vpnServersModalTitle',
      'manageVpnServerErrors',
      'manageVpnServerError',
      'vpnServersResponses',
    ]),
  },
  created() {
    this.$store.dispatch(GET_VPN_SERVERS, {})

    eventBus.$on('showToast/SettingsVpnServers', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.$store.commit(CLEAR_VPN_SERVERS)

    eventBus.$off('showToast/SettingsVpnServers')
  },
  methods: {
    selectServer(vpnServer) {
      this.$store.commit(SELECT_VPN_SERVER, vpnServer)
    },

    unselectVpnServer() {
      this.$store.commit(UNSELECT_VPN_SERVER)
    },

    openVpnServerParams(id) {
      this.$store.dispatch(GET_VPN_SERVER, { id })
        .then(data => {
          this.selectServer(data)
        })
        .finally(() => {
          this.showModal('manageModal')
        })
    },

    submitVpnServer() {
      this.$store.dispatch(MANAGE_VPN_SERVER)
        .then(() => {
          this.hideModal('manageModal')
        })
    },

    showModal(ref) {
      this.$refs[ref].show()
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    onHideAttempt(event) {
      const button = this.$refs['cancelManageModal']
      if (event.trigger == 'backdrop') {
        event.preventDefault()
        button.classList.add('btn-shake')
        setTimeout(() => { button.classList.remove('btn-shake') }, 500)
      }
    },

    getValidationState(field) {
      return this.manageVpnServerErrors.some(err => err.field == field) ? false : null
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_VPN_SERVER_ERRORS)
    },

    showDeletePopover(id) {
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('bv::show::popover', `deleteButton-${id}`)
    },

    hideDeletePopover(id) {
      this.$root.$emit('bv::hide::popover', `deleteButton-${id}`)
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_VPN_SERVER)
    },

    markForSwitch(vpnServer) {
      this.$store.commit(MARK_FOR_SWITCH_VPN_SERVER, vpnServer)
    },

    unmarkForSwitch() {
      this.$store.commit(UNMARK_FOR_SWITCH_VPN_SERVER)
    },

    handleStatusChange(vpnServer) {
      this.markForSwitch(vpnServer)

      if (vpnServer.active) {
        this.showModal('confirmModal')
      } else {
        this.switchVpnServer(vpnServer._id)
      }
    },

    switchVpnServer(id) {
      this.$store.dispatch(SWITCH_VPN_SERVER, { id })
    },

    addNewParameter(index) {
      this.$store.commit(ADD_NEW_VPN_SERVER_PARAM, index)
    },

    deleteParameter(index) {
      this.$store.commit(DELETE_VPN_SERVER_PARAM, index)
    },

    showToast(message, variant) {
      this.$bvToast.toast(message, {
        variant: variant,
        ...toastOptions
      })
    }
  }
}
</script>
